import {
  Persona,
  PersonaId,
  TokenStage,
  MarketplaceNearGraduation,
  usePersonaQuery,
  usePersonasQuery,
} from "@/api/personas.ts";
import { usePrivy } from "@privy-io/react-auth";
import { PersonaAvatar } from "@/components/PersonaAvatar.tsx";
import { Button } from "../ui/button";
import { Copy, LoaderCircle, Users } from "lucide-react";
import { convertISODateToRelativeTime } from "@/lib/date.ts";
import { Link } from "react-router-dom";
import { X } from "@/components/logos/X.tsx";
import { Solscan } from "../logos/Solscan";
import { toast } from "@/hooks/use-toast.ts";
import { formatLargeNumber } from "@/lib/utils.ts";
import { useQuery } from "@tanstack/react-query";
import { tokenQueries, useGraduatingTokensQuery } from "@/api/tokens.ts";

const graduationThreshold = import.meta.env.VITE_GRADUATION_THRESHOLD;
const ABOUT_TO_GRADUATE_THRESHOLD = 0.9;

function groupByStage(
  personas: Persona[],
  graduatingTokens: Record<PersonaId, any>,
) {
  const stageGroups: Record<
    TokenStage | MarketplaceNearGraduation,
    PersonaId[]
  > = {
    minted: [],
    "near-graduation": [],
    graduated: [],
  };

  for (const persona of personas) {
    const stage = persona.web3?.tokenStage;

    if (
      stage === "minted" &&
      graduatingTokens[persona.persona_id]?.pool_state?.depositedSol >=
        graduationThreshold * ABOUT_TO_GRADUATE_THRESHOLD
    ) {
      stageGroups["near-graduation"].push(persona.persona_id);
    } else if (stage) {
      stageGroups[stage].push(persona.persona_id);
    }
  }

  return stageGroups;
}

const COLUMNS = [
  {
    id: "minted",
    title: "New agents",
  },
  {
    id: "near-graduation",
    title: "About to graduate",
  },
  {
    id: "graduated",
    title: "Graduated",
  },
] as const;

export function GraduationTab({ searchQuery }: { searchQuery: string }) {
  const { authenticated } = usePrivy();
  const { data: personas = [] } = usePersonasQuery(authenticated);
  const { data: graduatingTokens } = useGraduatingTokensQuery();

  const filteredPersonas = personas.filter(
    (persona) =>
      persona.web3?.contractAddress &&
      (persona.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        persona.persona_id.toLowerCase().includes(searchQuery.toLowerCase())),
  );

  const groupedPersonas = groupByStage(
    filteredPersonas ?? [],
    graduatingTokens ?? {},
  );

  return (
    <div className="container mx-auto px-2">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        {COLUMNS.map((column, columnIndex) => (
          <div
            key={columnIndex}
            className="space-y-4 px-2 md:px-2 py-4 md:w-64 xl:w-[350px] 2xl:w-[500px] w-full max-w-full min-h-32"
          >
            <h2 className="text-lg font-bold uppercase">{column.title}</h2>

            <div className="flex md:flex-col flex-row max-h-[calc(100vh-12rem)] md:max-h-[calc(100vh-16rem)] gap-y-4 gap-x-2 overflow-y-auto">
              {groupedPersonas[column.id].map((personaId) => (
                <TokenStageCard key={personaId} personaId={personaId} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TokenStageCard({ personaId }: { personaId: PersonaId }) {
  const {
    data: persona,
    isPending: isPersonaPending,
    isError: isPersonaError,
  } = usePersonaQuery(usePrivy().authenticated, personaId);
  const {
    data: tokenPrice,
    isLoading: isTokenLoading,
    isError: isTokenError,
  } = useQuery({
    ...tokenQueries.tokenPrice({
      contractAddress: persona?.web3?.contractAddress ?? "",
      pool: persona?.web3?.pool ?? "",
      blockchain: persona?.web3?.blockchain ?? "",
    }),
    enabled: Boolean(
      persona?.web3?.contractAddress &&
        persona?.web3?.pool &&
        persona?.web3?.contractAddress,
    ),
  });

  const isPending = isPersonaPending || isTokenLoading;
  const isError = isPersonaError || isTokenError;

  if (!persona || isPending || isError) {
    return (
      <div className="border rounded-lg border-gray-700 p-3 pr-10 flex flex-col gap-y-4  md:max-w-64 xl:max-w-[350px] 2xl:max-w-[500px] min-h-20 items-center justify-center">
        {isPending ? (
          <LoaderCircle className="h-8 w-8 animate-spin stroke-accent m-auto" />
        ) : (
          <p className="text-muted-foreground text-sm">
            An error occurred. Please try again.
          </p>
        )}
      </div>
    );
  }

  return (
    <div
      className="border rounded-lg border-gray-700 p-3 flex flex-col gap-y-4 max-w-64 xl:max-w-[350px] 2xl:max-w-[500px]"
      data-testid={`agent-id-${persona.persona_id}`}
    >
      <div className="flex flex-row justify-between gap-x-2 max-w-full">
        <div className="flex flex-row items-center gap-x-2 min-w-0 basis-0 grow">
          <PersonaAvatar className="h-8" persona={persona} />
          <div className="flex flex-col gap-0 min-w-0 basis-0 grow">
            <div className="flex flex-row gap-1 items-center">
              <span className="truncate text-sm shrink" title={persona.name}>
                {persona.name}
              </span>
              {persona.web3?.metadata?.symbol ? (
                <div className="text-sm font-normal text-muted-foreground">
                  {persona.web3?.metadata?.symbol}
                </div>
              ) : null}
              {persona?.web3?.contractAddress ? (
                <Button
                  className="p-0 h-4 w-4 items-center justify-start text-muted-foreground hover:text-white hover:bg-transparent"
                  size="sm"
                  variant="ghost"
                  onClick={(e) => {
                    // Prevents the user from being redirected to the persona page
                    e.preventDefault();
                    if (persona?.web3?.contractAddress) {
                      navigator.clipboard
                        .writeText(persona.web3.contractAddress)
                        .then(() =>
                          toast({
                            description: "Address copied to clipboard.",
                          }),
                        );
                    }
                  }}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              ) : null}
            </div>
            {persona.created_at ? (
              <div
                onClick={(e) => {
                  // Prevents the user from being redirected to the persona page when clicking a social media link
                  e.stopPropagation();
                }}
                className="flex flex-row items-center gap-2 "
              >
                <span className="text-[#6fff1b] text-xs">
                  {convertISODateToRelativeTime(persona.created_at)}
                </span>
              </div>
            ) : null}
          </div>
        </div>
        <Link to={`/agents/${personaId}`}>
          <Button variant="outline">Buy</Button>
        </Link>
      </div>
      <div className="flex flex-row justify-between items-center">
        <div className="flex flex-row gap-x-2">
          {persona.web3?.contractAddress ? (
            <Link
              to={`https://solscan.io/token/${persona.web3.contractAddress}`}
              target="_blank"
              rel="noreferrer noopener"
            >
              <Solscan height={14} width={14} />
            </Link>
          ) : null}
          {persona.twitter ? (
            <Link
              to={`https://x.com/${persona.twitter}`}
              target="_blank"
              rel="noreferrer noopener"
            >
              <X height={12} width={12} fill="#ffffff" />
            </Link>
          ) : undefined}
        </div>
        <div className="flex flex-row gap-x-4 text-sm">
          <div className="flex flex-row gap-x-0.5 items-center">
            <Users className="text-gray-500 h-4" />
            {tokenPrice?.marketMetrics.holders
              ? formatLargeNumber(parseInt(tokenPrice.marketMetrics.holders), {
                  decimals: 0,
                  prefix: "",
                })
              : "N/A"}
          </div>
          <div className="flex flex-row gap-x-1">
            <span className="text-gray-500">V</span>
            {tokenPrice?.price.volume24h
              ? formatLargeNumber(tokenPrice.price.volume24h, { decimals: 0 })
              : "N/A"}
          </div>
          <div className="flex flex-row gap-x-1">
            <span className="text-gray-500">MC</span>
            {tokenPrice?.marketMetrics.marketCap
              ? formatLargeNumber(tokenPrice.marketMetrics.marketCap, {
                  decimals: 0,
                })
              : "N/A"}
          </div>
        </div>
      </div>
    </div>
  );
}
